import React, { useContext, useEffect, useState, useCallback } from 'react';
import Box from '@amzn/meridian/box';
import moment from 'moment';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import { FormattedMessage, useIntl } from 'react-intl';
import { renderUrl, urls } from '../../../config/Url';
import { EmptyProfile, ExtendedUser, Profile } from '../../../Types';
import { StateContext } from '../../../GlobalStateStore';
import { getCurrentWeek, getCurrentWeekBoundriesList } from '../../../Util';
import DataState from '../../../Types/DataState';
import DataSource from '../../../api';
import ProgressTracker, {
  ProgressTrackerStep,
} from '@amzn/meridian/progress-tracker';
import MeetsTheBar from '../../../commons/formLibrary/MeetsTheBar';
import styles from './TransactionMonitoring.module.css';
import YesCheck from '../../../commons/formLibrary/YesCheck';
import NoX from '../../../commons/formLibrary/NoX';
import {
  grabExtraFieldByID,
  getDefaultMessageByID,
  mergeFeedbackIntoContactDetails,
  isFormReviewedOrDelivered,
  getFormIdForDraftLookup,
  filterOptionsByVerticalType,
} from '../../../Util/Form/helpers';
import {
  QuestionOption,
  DropdownItem,
  Section,
  Question,
  PageItem,
  StepperBulletPointConfig,
} from '../../../Util/Form/formData.types';
import {
  DynamicFormAnswerInput,
  I18NContentInput,
  FormDetailsInput,
} from '../../../Util/Form/backendFormData.types';
import RenderDropdownsFromData from '../../../Util/Form/RenderDropdownsFromData';
import {
  ConsolidatedContact,
  ContactDetailsInput,
  ContactStatusOptionsObject,
  ContactViewTabObject,
  GoodRecommendationOptionsObject,
  I18NContent,
  RecommendedContactStatusOptionsObject,
} from '../../../api/CoachingNotes/schema';
import { getLocaleString } from '../../../translations/index';
import ContactReviewFeedbackModal from '../../ContactReview/Modal/FeedbackModal/ContactReviewFeedbackModal';
import {
  FormSaveType,
  StepperProgressTypes,
} from '../../../Util/Form/formData.constants';
import { BarRaiseChoices } from '@amzn/aet-contactrecommendation-kit';
import Text from '@amzn/meridian/text';
import {
  fetchContactStatus,
  getFormClickstreamTag1AndTag2,
  handleRealtimeHMDContact,
  OUTLIER_INGRESS,
} from '../utils/helper';
import TimeoutToast from '../../CoachingForm/AutoSave/TimeoutToast';
import PerformancePortalContext from '../../PerformancePortalContext';
import { ContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/types';
import { EmptyContactStatus } from '../../Dashboard/DashboardComponents/HmdDetails/helper';
import { useNavigate } from 'react-router';
import DraftSavedToast from '../../../Util/Form/DraftSavedToast';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import {
  FORM_PREFIXES,
  FORM_SECTION_IDS,
  FORM_DISPLAY_NAMES,
  SPECIAL_QUESTION_IDS,
} from '../../../Util/Form/formSectionConstants';
import {
  findQuestionAcrossSections,
  processAllDropdownSections,
} from '../../../Util/Form/sectionUtils';
import { convertToVerticalOption, D2_VERTICALS } from '../../../Util/Vertical';

interface TransactionMonitoringFormState {
  dropdownItems: DropdownItem[];
  stepperProgress: { [key: string]: StepperProgressTypes };
  pages: PageItem[];
  currentPage: number;
  questionsAnswered: { [key: string]: AnsweredQuestionItem };
  nextButtonDisabled: boolean;
  stepperBulletPoints: StepperBulletPointConfig;
  contactInfo: ConsolidatedContact;
  nextButtonRefresh: number;
}

interface TransactionMonitoringFormProps {
  userId: string | undefined;
  contactId: string | undefined;
  ccxDetails?: string;
  passedProfile?: ExtendedUser;
  selectedVertical: string;
  formType: string;
  ccxDetailsInfo: any;
  ccxRatings?: string | null;
  ingress: string | null;
}

type AnsweredQuestionItem = {
  comments: string;
  checkedValue: boolean | undefined;
};

type MetTheBarItem = {
  metTheBar: I18NContent[];
  belowTheBar: I18NContent[];
};

type FormInformation = {
  metTheBarCriteria: { [key: string]: MetTheBarItem };
  stepperBulletPoints: { [key: string]: StepperBulletPointConfig };
  defaultDropdownItems: DropdownItem[];
};

const csaFields = (
  type: string,
  subtype: string,
  contentID: string,
  ingress: string | null,
  isCCX?: boolean
) => ({
  'data-csa-c-type': type,
  'data-csa-c-slot-id': `'tx-monitoring-${subtype}-button-slot'`,
  'data-csa-c-content-id': `tx-monitoring-${subtype}-button-content-id-${contentID}`,
  ...getFormClickstreamTag1AndTag2(ingress, isCCX),
  'data-csa-c-tag3': `Transaction Monitoring`,
});

const MAX_STEPS_PER_PAGE = 3;

// GGGG refers to the year of the week. This may be different from YYYY
// When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
// In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
const startWeek = moment().subtract(4, 'w').format('GGGG-WW');
const endWeek = moment().format('GGGG-WW');
const date = getCurrentWeekBoundriesList();

const TransactionMonitoringForm: React.FC<TransactionMonitoringFormProps> = ({
  userId,
  contactId,
  selectedVertical,
  formType,
  passedProfile,
  ccxDetailsInfo,
  ccxRatings,
  ingress,
}) => {
  const { performancePortalContextValue } = useContext(
    PerformancePortalContext
  );
  const globalState = useContext(StateContext);
  const navigate = useNavigate();
  const initialProfile = passedProfile
    ? { ...EmptyProfile, user: passedProfile }
    : EmptyProfile;
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const [emailChecked, setEmailChecked] = useState(false);
  const [, setProfileState] = useState(DataState.LOADING);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix, isAdmin, requesterUserID } = inspireOptions.data;
  const apiNaws = DataSource.getDataFeed('NAWS');
  const [showContactReviewFeedbackModal, setShowContactReviewFeedbackModal] =
    useState<{ open: boolean; status?: FormSaveType }>({
      open: false,
    });
  const [stepperKeys, setStepperKeys] = useState<string[]>([]);
  const [contactIDs, setContactIDs] = useState<string[]>([]);

  const [contactList, setContactList] = useState<ConsolidatedContact[]>([]);
  const [contactListState, setContactListState] = useState(DataState.LOADING);
  const [contactStatusDetails, setContactStatusDetails] =
    useState<ContactStatus>(EmptyContactStatus);
  const [contactStatusDetailsState, setContactStatusDetailsState] = useState(
    DataState.LOADING
  );
  const [txData, setTxData] = useState<void | any>();
  const [, setTxDataState] = useState(DataState.LOADING);
  const [txMonitoringData, setTxMonitoringData] = useState<any>(undefined);
  const [nextButtonRefresh, setNextButtonRefresh] = useState(0);
  const [formInformation, setFormInformation] = useState<
    Partial<FormInformation>
  >({});
  const [formStatusCode, setFormStatusCode] = useState({});
  const [formStatusCodeState, setFormStatusCodeState] = useState(
    DataState.INITIALIZED
  );
  const [outlierResp, setOutlierResp] = useState({});
  const [outlierRespState, setOutlierRespState] = useState(
    DataState.INITIALIZED
  );
  const [formState, setFormState] = useState<
    Partial<TransactionMonitoringFormState> & { dropdownItems: DropdownItem[] }
  >({
    dropdownItems: [],
    currentPage: 0,
    nextButtonDisabled: true,
  });

  // Toast
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showDraftSavedToast, setShowDraftSavedToast] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [exceptionMessage, setExceptionMessage] = useState('');

  const intl = useIntl();

  const refreshContacts = useCallback(() => {
    const { userName } = profile.user;
    if (userName !== '') {
      const userID = userId ? `${cscEntityPrefix}:${userId}` : '';
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        userName,
        startWeek,
        endWeek
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [profile, userId]);

  useEffect(() => {
    if (
      formStatusCodeState === DataState.ERROR ||
      outlierRespState === DataState.ERROR
    ) {
      setShowErrorToast(true);
    }

    if (formStatusCodeState === DataState.LOADED && isSavingDraft) {
      setShowDraftSavedToast(true);
    }
    setIsSavingDraft(false);
  }, [formStatusCodeState, outlierRespState]);

  useEffect(() => {
    setFormState(prevState => ({
      ...prevState,
      contactInfo:
        contactList?.filter(c => c.contactID === contactId)?.[0] ?? {},
      dropdownItems: handleSpecialDropdownItems(
        formState?.dropdownItems ?? [],
        txMonitoringData
      ),
    }));
  }, [txMonitoringData, contactId, selectedVertical]);

  useEffect(() => {
    // Logic Below is about auto-pupulate value after saving
    if (contactId) {
      const contactInfo = contactList.find(
        contact => contact.contactID === contactId
      );
      if (contactInfo) {
        // Only load draft data that matches the current form type
        // This prevents loading old TMF draft data when form type changes to D2AS Contact Review due to feature flag
        const targetFormID = getFormIdForDraftLookup(formType);

        const formReviews = contactInfo?.formReviews?.filter(
          f => f.formID === targetFormID
        );

        const questionsAnswered = formReviews?.[0]?.formAnswer.reduce(
          (
            acc: { [key: string]: AnsweredQuestionItem },
            answer: DynamicFormAnswerInput
          ) => {
            acc[answer.questionText.defaultMessage] = {
              comments: answer.answerText || '',
              checkedValue: answer?.selectedOption?.defaultMessage
                ? answer?.selectedOption?.defaultMessage === 'Met the bar'
                : undefined,
            };
            return acc;
          },
          {}
        );

        // Get all possible dropdown questions from both TX_MONITORING and D2_MONITORING sections
        const txDropdownQuestions =
          txMonitoringData?.SECTIONS?.filter(
            (s: Section) =>
              s.SECTION_ID === FORM_SECTION_IDS.TX_MONITORING_DROPDOWNS
          )?.[0]?.QUESTIONS ?? [];

        const d2DropdownQuestions =
          txMonitoringData?.SECTIONS?.filter(
            (s: Section) =>
              s.SECTION_ID === FORM_SECTION_IDS.D2_MONITORING_DROPDOWNS
          )?.[0]?.QUESTIONS ?? [];

        const allDropdownQuestions = [
          ...txDropdownQuestions,
          ...d2DropdownQuestions,
        ];

        const dropdownItems = formReviews?.[0]?.formAnswer
          .filter((answer: any) =>
            allDropdownQuestions.some(
              ({ QUESTION_ID }) => QUESTION_ID === answer?.questionID
            )
          )
          .map((answer: any) => {
            let type = answer?.questionID?.toLowerCase();
            if (type.includes(FORM_PREFIXES.TX_MONITORING)) {
              type = type.replace(FORM_PREFIXES.TX_MONITORING, '');
            } else if (type.includes(FORM_PREFIXES.D2_MONITORING)) {
              type = type.replace(FORM_PREFIXES.D2_MONITORING, '');
            }
            // we need to find the corresponding question options in data
            const options =
              allDropdownQuestions.length === 0
                ? [answer?.selectedOption]
                : allDropdownQuestions?.filter(
                    (d: any) =>
                      d.QUESTION_TEXT.TRANSLATION_ID ===
                      answer?.questionText?.translationID
                  )?.[0]?.QUESTION_OPTIONS;

            return {
              type,
              placeholder: {
                TRANSLATION_ID: answer?.questionText.translationID,
                DEFAULT_MESSAGE: answer?.questionText.defaultMessage,
              },
              options,
              selected: answer?.selectedOption?.defaultMessage,
            };
          });

        setFormState(prevFormState => ({
          ...prevFormState,
          contactInfo: contactInfo ?? prevFormState?.contactInfo,
          questionsAnswered: questionsAnswered ?? {},
          dropdownItems: dropdownItems ?? prevFormState?.dropdownItems,
        }));
        if (!dropdownItems) {
          // might be old data or empty. need to set state to original after api response
          setTxData({ ...txData });
        }
      }
    }
  }, [contactId, contactList, formType, txMonitoringData]);

  useEffect(() => {
    fetchContactStatus(
      ingress,
      profile,
      cscEntityPrefix,
      date,
      apiNaws,
      setContactStatusDetails,
      setContactStatusDetailsState
    );
  }, [ingress, profile]);

  useEffect(() => {
    handleRealtimeHMDContact(
      contactId,
      ingress,
      contactList,
      contactListState,
      contactStatusDetailsState,
      profile,
      cscEntityPrefix,
      contactStatusDetails,
      setContactList,
      setContactListState
    );
  }, [
    contactId,
    ingress,
    contactList,
    profile,
    contactListState,
    contactStatusDetailsState,
  ]);

  useEffect(() => {
    const data: any = txData?.[0];
    if (data) {
      setTxMonitoringData(data);
      // This is where transaction monitoring form dropdowns are initially set for the formState
      let defaultDropdownItems = processAllDropdownSections(data.SECTIONS);

      // special cases can go here
      defaultDropdownItems = handleSpecialDropdownItems(
        defaultDropdownItems,
        data
      );

      const stepperBulletPoints: { [key: string]: StepperBulletPointConfig } =
        data.SECTIONS.filter(
          (section: Section) => section.TYPE === 'BULLET_POINT'
        ).reduce((acc: any, section: any) => {
          if (section?.QUESTION_TEXT?.DEFAULT_MESSAGE) {
            const points = section.QUESTIONS.map((question: Question) => {
              return {
                defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
                translationID: question.QUESTION_TEXT.TRANSLATION_ID,
              } as I18NContent;
            });
            acc[section.QUESTION_TEXT.DEFAULT_MESSAGE] = {
              points: points,
              trackLength:
                section.QUESTIONS.length <= 2
                  ? 6
                  : Math.ceil(section.QUESTIONS.length / 2) * 5,
              customClass: '',
            };
          }
          return acc;
        }, {});

      // map the stepper bullet points to criteria on right side
      const metTheBarCriteria: { [key: string]: MetTheBarItem } =
        data.SECTIONS.filter(
          ({ TYPE }: { TYPE: string }) => TYPE === 'BULLET_POINT'
        ).reduce((acc: any, section: Section) => {
          section.QUESTIONS.forEach(question => {
            if (
              section?.QUESTION_TEXT?.DEFAULT_MESSAGE &&
              question.QUESTION_OPTIONS
            ) {
              const metTheBar = question.QUESTION_OPTIONS.filter(
                (option: QuestionOption) =>
                  option.TRANSLATION_ID.includes('met_bar')
              ).map(option => {
                return {
                  defaultMessage: option.DEFAULT_MESSAGE,
                  translationID: option.TRANSLATION_ID,
                } as I18NContent;
              });

              const belowTheBar = question.QUESTION_OPTIONS.filter(
                (option: QuestionOption) =>
                  option.TRANSLATION_ID.includes('below_bar')
              ).map(option => {
                return {
                  defaultMessage: option.DEFAULT_MESSAGE,
                  translationID: option.TRANSLATION_ID,
                } as I18NContent;
              });
              acc[question.QUESTION_TEXT.DEFAULT_MESSAGE] = {
                metTheBar: metTheBar,
                belowTheBar: belowTheBar,
              };
            }
          });
          return acc;
        }, {});

      setFormInformation(prevFormInformation => ({
        ...prevFormInformation,
        defaultDropdownItems,
        stepperBulletPoints,
        metTheBarCriteria,
      }));

      setStepperKeys(Object.keys(stepperBulletPoints));

      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: prevFormState.dropdownItems?.length
          ? prevFormState.dropdownItems // Preserve existing saved values
          : defaultDropdownItems, // Only use defaults if no saved values
      }));
    }
  }, [txData]);

  // Set the initial formState from operations
  useEffect(() => {
    const stepperProgress = stepperKeys.reduce(
      (prev, curr) => ({
        ...prev,
        [curr]:
          Object.keys(prev).length === 0
            ? StepperProgressTypes.PRESENT
            : StepperProgressTypes.FUTURE,
      }),
      {}
    );

    const pages: PageItem[] = [];

    stepperKeys.forEach(key => {
      const points = formInformation?.stepperBulletPoints?.[key]?.points;
      if (!points) {
        return;
      }
      for (let i = 0; i < points.length; i += MAX_STEPS_PER_PAGE) {
        const page: { text: I18NContent }[] = [];
        let innerIndex = i;
        while (points[innerIndex] && innerIndex < i + MAX_STEPS_PER_PAGE) {
          page.push({ text: points[innerIndex] });
          innerIndex++;
        }
        pages.push({ points: page, type: key });
      }
    });

    setFormState(prevFormState => ({
      ...prevFormState,
      stepperProgress,
      pages,
    }));
  }, [formInformation]);

  // Get profile data for user on page load
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      if (!passedProfile) {
        const queryMigrationVariables = apiNaws.prepareQuery(
          getCurrentWeek(),
          userId,
          cscEntityPrefix
        );
        apiNaws.getProfileData(
          queryMigrationVariables,
          setProfile,
          setProfileState
        );
      }

      // Determine template based on FORM TYPE, not just vertical
      // This ensures backward compatibility with old drafts that have D2AS vertical + TMF form type
      const verticalOption = convertToVerticalOption(selectedVertical);
      const isD2FlagEnabled = isFeatureEnabled(Feature.ENABLE_D2_FORM);

      let verticalToUse = 'Total'; // Default to TMF template

      // Only use D2AS template if BOTH conditions are met:
      // 1. D2AS vertical AND D2 flag enabled
      // 2. Form type is specifically "D2AS Contact Review"
      if (
        verticalOption &&
        D2_VERTICALS.has(verticalOption) &&
        isD2FlagEnabled &&
        formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW
      ) {
        verticalToUse = selectedVertical;
      }

      apiNaws.getFormTemplateData(
        { VERTICAL: verticalToUse },
        setTxData,
        setTxDataState
      );
    }
  }, [inspireOptions.state, userId, selectedVertical, formType]);

  useEffect(() => {
    refreshContacts();
  }, [profile]);

  useEffect(() => {
    if (contactList?.length > 0) {
      if (contactId) {
        const contactInfo = contactList?.find(c => c.contactID === contactId);
        setFormState(prevFormState => ({ ...prevFormState, contactInfo }));
      }
      setContactIDs(
        contactList
          .filter(
            contact =>
              contact.status ===
              RecommendedContactStatusOptionsObject.NOTREVIEWED
          )
          .map(c => c.contactID)
      );
    }
  }, [contactList]);

  useEffect(() => {
    const { currentPage, pages, questionsAnswered } = formState;
    if (currentPage !== undefined && pages && questionsAnswered) {
      setFormState({ ...formState, nextButtonRefresh });
      if (nextButtonRefresh === 0) {
        updateStepperProgress();
      }
    }
  }, [formState.questionsAnswered, nextButtonRefresh]);

  const formStepperProgress = formState?.stepperProgress ?? {};

  // Find the tenet that's present, or, if all are past, take the very last step (all are completed)
  const activeTenet =
    formState?.pages?.[formState?.currentPage ?? 0]?.type ??
    Object.keys(formStepperProgress)?.reverse()?.[0];

  useEffect(() => {
    handleSelectDropdownValue('tx_monitoring_vertical')(selectedVertical);
  }, [selectedVertical]);

  useEffect(() => {
    const items = formState?.dropdownItems;
    if (items && selectedVertical) {
      const index = items.findIndex(t => t.type === 'vertical');
      const newItem = { ...items[index], selected: selectedVertical };
      items[index] = newItem;

      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: items,
      }));
    }
  }, [formState.dropdownItems, selectedVertical]);

  const handleSelectDropdownValue = (type: string) => (selected: string) => {
    const items = formState?.dropdownItems;
    if (items) {
      const index = items.findIndex(t => t.type === type);
      const newItem = { ...items[index], selected };
      items[index] = newItem;
      if (items[index].type?.includes('vertical')) {
        items[index].selected = selectedVertical;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        dropdownItems: items,
      }));
    }
  };

  let totalPoints = 0;
  for (const p of formState?.pages ?? []) {
    totalPoints += p?.points?.length;
  }

  const acceptableDropdowns =
    formState?.dropdownItems?.filter(d => d?.options?.length > 0)?.length ?? 0;
  const totalRequiredPoints = acceptableDropdowns + totalPoints;

  const questionsAnsweredKeys = Object.keys(
    formState?.questionsAnswered ?? []
  )?.filter((c: any) => !c.includes('*'));

  const unselectedDropdown =
    formState?.dropdownItems?.filter(item => item.selected === undefined)
      .length ?? 0;

  const canSave =
    questionsAnsweredKeys?.length + acceptableDropdowns >=
      totalRequiredPoints &&
    questionsAnsweredKeys?.filter((key: string) => {
      const value: AnsweredQuestionItem | undefined =
        formState?.questionsAnswered?.[key];
      return value?.checkedValue === undefined;
    })?.length === 0 &&
    unselectedDropdown === 0;

  const isReviewedOrDelivered = isFormReviewedOrDelivered(
    contactId,
    formState?.contactInfo?.contactStatus
  );

  const handleSaveLogic = async (
    status: FormSaveType,
    feedbackDetails?: ContactDetailsInput
  ) => {
    // Extract the necessary data from the form state
    const { dropdownItems = [], questionsAnswered } = formState;
    const {
      FORM_ID: formID,
      VERSION: version,
      SECTIONS,
      TITLE: title,
    } = txMonitoringData;
    const formAnswers: DynamicFormAnswerInput[] = [];

    // Map dropdown items to the appropriate format
    dropdownItems.forEach((item: DropdownItem) => {
      const question = findQuestionAcrossSections(
        SECTIONS,
        item.type?.toUpperCase() || ''
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        const specialFormTypeCase = {
          defaultMessage: formType,
          translationID:
            formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW
              ? 'cs_perfportal_d2as_contact_review_form_title'
              : 'cs_perfportal_tx_monitoring_form_type_value',
        };

        let selectedOption = item.selected;
        if (questionText.defaultMessage.includes('Vertical')) {
          selectedOption = selectedVertical;
        }

        let selectedOptionText = question.QUESTION_OPTIONS?.find(
          option => option.DEFAULT_MESSAGE === selectedOption
        );

        const selectedOptionInput: I18NContentInput | undefined =
          selectedOptionText
            ? {
                translationID: selectedOptionText.TRANSLATION_ID,
                defaultMessage: selectedOptionText.DEFAULT_MESSAGE,
              }
            : question.QUESTION_ID ===
                (formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW
                  ? SPECIAL_QUESTION_IDS.D2_MONITORING_CONTACT_REVIEW_FORM_TYPE
                  : SPECIAL_QUESTION_IDS.TX_MONITORING_CONTACT_REVIEW_FORM_TYPE)
              ? specialFormTypeCase
              : undefined;

        const valueOfOption =
          selectedOptionText &&
          selectedOptionText.VALUE !== undefined &&
          selectedOptionText.VALUE !== null
            ? { value: selectedOptionText.VALUE }
            : {};

        formAnswers.push({
          questionID: question.QUESTION_ID,
          questionText,
          selectedOption: selectedOptionInput,
          ...valueOfOption,
        });
      }
    });

    const duplicatedDownItemsPlaceHolders = formState?.dropdownItems?.map(
      p => p.placeholder.DEFAULT_MESSAGE
    );

    // Map the questions answered to the appropriate format
    Object.keys(questionsAnswered ?? {}).forEach((key: string) => {
      if (duplicatedDownItemsPlaceHolders?.includes(key)) {
        // skip all dropdown items asl they are already accounted for
        return;
      }

      const questionData: AnsweredQuestionItem | undefined =
        questionsAnswered?.[key];

      if (
        questionData?.checkedValue === undefined &&
        status !== FormSaveType.DRAFT
      ) {
        return;
      }

      const section: Section | undefined = SECTIONS.find((section: Section) =>
        section.QUESTIONS.some(
          (question: Question) => question.QUESTION_TEXT.DEFAULT_MESSAGE === key
        )
      );
      const question: Question | undefined = section?.QUESTIONS.find(
        (question: Question) => question.QUESTION_TEXT.DEFAULT_MESSAGE === key
      );

      if (question) {
        const questionText: I18NContentInput = {
          translationID: question.QUESTION_TEXT.TRANSLATION_ID,
          defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
        };

        const answerText = questionData?.comments;

        let formDataItem: DynamicFormAnswerInput = {
          questionID: question.QUESTION_ID,
          questionText,
          answerText,
        };

        // if this question was NOT answered, do not save it at all
        // therefore, only populate when checkedValue is present
        if (questionData?.checkedValue !== undefined) {
          const value = questionData?.checkedValue ? 1 : 0;
          const selectedOption = {
            defaultMessage: value ? 'Met the bar' : 'Below the bar',
            translationID: value
              ? 'cs_perfportal_contactReview_barRaisedQuestion_metTheBar'
              : 'cs_perfportal_barRaisedQuestion_belowTheBar',
          };
          formDataItem = {
            ...formDataItem,
            selectedOption,
            value: value
              ? BarRaiseChoices.MET_THE_BAR
              : BarRaiseChoices.BELOW_THE_BAR,
          };
        }

        formAnswers.push(formDataItem);
      }
    });

    const formDetailsInput: FormDetailsInput = {
      formID: formID,
      version: version,
      formAnswers,
      contactStatus: status,
    };

    const selectedContact = contactList.filter(
      c => c.contactID === contactId
    )[0];
    const vertical = formState?.dropdownItems?.find(
      d => d.type === 'vertical'
    )?.selected;

    const contactDetailsInput = mergeFeedbackIntoContactDetails(
      feedbackDetails,
      selectedContact!,
      vertical,
      formDetailsInput,
      ingress ?? ''
    );
    const updateFormReviewInput = {
      userName: profile.user.userName,
      userID: selectedContact?.userID,
      year: selectedContact?.calYear,
      week: selectedContact?.calWeek,
      contactID: contactId,
      reviewStatus: ContactStatusOptionsObject.REVIEWED,
      contactDetailsInput,
      isReviewedByCSA: false,
      contactStatus: status,
      formDetails: formDetailsInput,
      inAppFlag: status === ContactStatusOptionsObject.DELIVERED,
      emailFlag: emailChecked,
      locale: getLocaleString(),
    };

    try {
      // Send the data to the backend
      await apiNaws.updateFormReview(
        updateFormReviewInput,
        setFormStatusCode,
        setFormStatusCodeState,
        setExceptionMessage
      );
      if (
        ingress === OUTLIER_INGRESS &&
        isFeatureEnabled(Feature.OUTLIER_MANAGEMENT) &&
        updateFormReviewInput.contactDetailsInput.goodRecommendation ===
          GoodRecommendationOptionsObject.NO &&
        status === FormSaveType.DELIVERED
      ) {
        const writeOutlierExceptionInput =
          apiNaws.prepareQueryForWriteOutlierException(
            requesterUserID, //userId
            profile.user.userName, //deliveredToUsername
            selectedContact?.userID, //deliveredToUserId
            contactId, //contactId
            selectedContact?.calYear, //outlierCalYear
            selectedContact?.calWeek, //outlierCalWeek
            feedbackDetails?.justification //justification
          );
        await apiNaws.writeOutlierException(
          writeOutlierExceptionInput,
          setOutlierResp,
          setOutlierRespState
        );
      }
      if (status === FormSaveType.DRAFT) {
        setIsSavingDraft(true);

        let redirectURL = `${urls.contactReview}/${userId}/${contactId}`;
        const urlParams = new URLSearchParams(window.location.search);
        if (ccxRatings) {
          urlParams.set('ccxRatings', ccxRatings);
        }
        if (ingress === 'realtimeHMD') {
          urlParams.set('ingress', 'realtimeHMD');
        }
        if (ingress === OUTLIER_INGRESS) {
          urlParams.set('ingress', OUTLIER_INGRESS);
        }
        redirectURL += `?${urlParams}`;
        navigate(renderUrl(redirectURL), { replace: true });
      }
    } catch (error) {
      console.error('Error saving data', error);
    }
  };

  const openFeedbackModal = () => {
    setShowContactReviewFeedbackModal({
      open: true,
      status: FormSaveType.DELIVERED,
    });
  };

  const handleSaveAsDraft = () => handleSaveLogic(FormSaveType.DRAFT);
  const handleSave = async (feedbackDetails: ContactDetailsInput) => {
    if (!canSave) {
      return;
    }
    handleSaveLogic(FormSaveType.DELIVERED, feedbackDetails);
  };

  const handleClickBackButton = () => {
    const { currentPage } = formState;
    if (!currentPage) {
      return;
    }
    setFormState(prevFormState => ({
      ...prevFormState,
      currentPage: currentPage - 1,
    }));
  };

  const handleClickNextPage = () => {
    const { currentPage, pages, stepperProgress } = formState;
    if (currentPage !== undefined && pages && stepperProgress) {
      const newStepperProgress = { ...stepperProgress };
      const currentPageType = pages[currentPage].type;
      if (currentPage === pages.length - 1) {
        // On the last page, only need to set the last stepper to past
        newStepperProgress[currentPageType] = StepperProgressTypes.PAST;
        setFormState(prevFormState => ({
          ...prevFormState,
          stepperProgress: newStepperProgress,
        }));
        return;
      }

      const nextPage = currentPage + 1;
      const newPageType = pages[nextPage].type;
      // If the page changed the stepper step, we need to update that as well
      if (newPageType !== currentPageType) {
        newStepperProgress[currentPageType] = StepperProgressTypes.PAST;
        newStepperProgress[newPageType] = StepperProgressTypes.PRESENT;
      }
      setFormState(prevFormState => ({
        ...prevFormState,
        currentPage: nextPage,
        stepperProgress: newStepperProgress,
      }));
      setNextButtonRefresh(nextButtonRefresh + 1);
    }
  };

  const handleQuestionAnswered =
    (item: string) => (data: AnsweredQuestionItem) => {
      const { questionsAnswered } = formState;
      const copy = { ...questionsAnswered };
      copy[item] = data;
      setFormState(prevFormState => ({
        ...prevFormState,
        questionsAnswered: copy,
      }));
    };

  const handleFeedbackSubmitted = async (
    feedbackDetails: ContactDetailsInput
  ): Promise<any> => {
    const status = showContactReviewFeedbackModal?.status;
    setShowContactReviewFeedbackModal({
      open: false,
      status: undefined,
    });
    switch (status) {
      case FormSaveType.DELIVERED:
        await handleSave(feedbackDetails);
        break;
      case FormSaveType.DRAFT:
      default:
        await handleSaveAsDraft();
        break;
    }
    let redirectUrl = `${urls.coachingNotes}/${userId}?href=${ContactViewTabObject.REVIEWED}`;

    if (ingress === 'realtimeHMD') {
      redirectUrl += `&ingress=realtimeHMD`;
    } else if (ingress === OUTLIER_INGRESS) {
      redirectUrl = urls.outlierManagement;
    }

    setTimeout(() => {
      navigate(renderUrl(redirectUrl), { replace: true });
    }, 3000);
  };

  const handleSpecialDropdownItems = (
    dropdownItems: DropdownItem[],
    data: any
  ): DropdownItem[] => {
    dropdownItems = dropdownItems?.map((d: DropdownItem) => {
      switch (d.type) {
        case 'contact_review_form_type':
          // Use helper function to filter options based on vertical type
          d.options = filterOptionsByVerticalType(
            selectedVertical,
            d.options || []
          );
          d.selected = formType;
          break;
      }
      return d;
    });
    return dropdownItems;
  };

  const updateStepperProgress = () => {
    const map: { [key: string]: number } = {};
    (formState?.pages ?? []).forEach(
      p => (map[p.type] = (map[p.type] ?? 0) + p.points.length)
    );
    const totalCompleted = Object.keys(
      formState?.questionsAnswered ?? {}
    )?.filter(k => !k.includes('*')).length;
    const stepperProgressMap: { [key: string]: StepperProgressTypes } = {};
    // build the progress now
    let previousMinimum = 0;
    let totalCounted = 0;
    Object.keys(map).forEach(mapKey => {
      const value = map[mapKey];
      totalCounted += value;
      if (totalCompleted > totalCounted) {
        stepperProgressMap[mapKey] = StepperProgressTypes.PAST;
      } else if (
        totalCompleted === totalCounted ||
        totalCompleted > previousMinimum
      ) {
        stepperProgressMap[mapKey] = StepperProgressTypes.PRESENT;
      } else {
        stepperProgressMap[mapKey] = StepperProgressTypes.FUTURE;
      }
      previousMinimum = totalCounted;
    });
    setFormState({
      ...formState,
      stepperProgress: stepperProgressMap,
      nextButtonDisabled: totalCompleted < 3,
    });
  };

  // Determine which form prefix and section to use based on form type
  const formPrefix =
    formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW
      ? FORM_PREFIXES.D2_MONITORING
      : FORM_PREFIXES.TX_MONITORING;

  const dropdownSectionID =
    formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW
      ? FORM_SECTION_IDS.D2_MONITORING_DROPDOWNS
      : FORM_SECTION_IDS.TX_MONITORING_DROPDOWNS;

  const dropdowns = (
    <RenderDropdownsFromData
      profile={profile}
      data={txMonitoringData}
      formPrefix={formPrefix}
      dropdownSectionID={dropdownSectionID}
      styles={styles}
      handleSelectDropdownValue={handleSelectDropdownValue}
      formState={formState}
      setFormState={setFormState}
    />
  );

  const getStepperLabel = (key: string) => (
    <span
      className={
        formState?.stepperProgress?.[key] === StepperProgressTypes.PRESENT
          ? styles.activeStepperLabel
          : styles.stepperLabel
      }
    >
      <FormattedMessage id='1' defaultMessage={key} />
    </span>
  );

  const stepperLeftColumn = (
    <ProgressTracker type='theme' compact={false} autoTrackLength={true}>
      {stepperKeys.map((key, index) => (
        <ProgressTrackerStep
          key={key}
          type={
            formState?.stepperProgress?.[key] ?? StepperProgressTypes.FUTURE
          }
          label={getStepperLabel(key)}
          showLabel
          alert='none'
        >
          <ul
            className={
              index === stepperKeys.length - 1 ? undefined : styles.ulMarginAuto
            }
          >
            {formInformation?.stepperBulletPoints?.[key]?.points.map(
              bulletPoint =>
                formState?.questionsAnswered?.[bulletPoint.defaultMessage]
                  ?.checkedValue !== undefined ? (
                  <li
                    key={bulletPoint.defaultMessage}
                    className={styles.noBulletPoint}
                  >
                    {formState?.questionsAnswered?.[bulletPoint.defaultMessage]
                      ?.checkedValue ? (
                      <span className={styles.iconMargin}>
                        <YesCheck
                          tooltip='Meets the bar'
                          className={styles.yesCheckGreen}
                        />
                      </span>
                    ) : (
                      <span className={styles.iconMargin}>
                        <NoX
                          tooltip='Below the bar'
                          className={styles.noXRed}
                        />
                      </span>
                    )}
                    <FormattedMessage
                      id={bulletPoint.translationID}
                      defaultMessage={bulletPoint.defaultMessage}
                    />
                  </li>
                ) : (
                  <li
                    key={bulletPoint.defaultMessage}
                    className={styles.stepperBulletPoint}
                  >
                    <FormattedMessage
                      id={bulletPoint.translationID}
                      defaultMessage={bulletPoint.defaultMessage}
                    />
                  </li>
                )
            )}
          </ul>
        </ProgressTrackerStep>
      ))}
    </ProgressTracker>
  );

  const stepperRightColumn = (
    <Box
      type='outline'
      spacingInset='400'
      width='auto'
      height='840px'
      className={styles.tenantContainer}
    >
      <span className={styles.activeTenantHeading}>
        Tenet : {activeTenet ?? ''}
      </span>
      {formState?.pages?.[formState?.currentPage ?? 0]?.points.map(point => (
        <MeetsTheBar
          key={point.text.defaultMessage}
          heading={point.text}
          bulletPoints={
            formInformation?.metTheBarCriteria?.[point.text.defaultMessage]
          }
          onChange={handleQuestionAnswered(point.text.defaultMessage)}
          data={formState?.questionsAnswered?.[point.text.defaultMessage]}
          forceResetState={formState?.currentPage}
          placeholder={intl.formatMessage({
            id: 'cs_perfportal_contact_review_table_note_placeholder',
            defaultMessage: 'Add notes here (Optional)',
            description: 'Note',
          })}
          disabled={isReviewedOrDelivered}
        />
      ))}
      <div className={styles.rightButtonsContainer}>
        <div className={styles.backButtonContainer}>
          <Button
            type='secondary'
            disabled={formState?.currentPage === 0}
            onClick={handleClickBackButton}
            {...csaFields(
              'button',
              'back',
              `cs_perfportal_tx_monitoring_back-${formState?.currentPage}`,
              ingress,
              !!ccxDetailsInfo
            )}
          >
            <FormattedMessage
              id='cs_perfportal_tx_monitoring_back'
              defaultMessage={getDefaultMessageByID(
                txMonitoringData,
                'cs_perfportal_tx_monitoring_back'
              )}
            />
          </Button>
        </div>
        <div className={styles.nextButtonContainer}>
          {(formState?.currentPage ?? 0) !==
            (formState?.pages?.length ?? 1) - 1 && (
            <Button
              disabled={formState?.nextButtonDisabled}
              onClick={handleClickNextPage}
              {...csaFields(
                'button',
                'next',
                `cs_perfportal_tx_monitoring_next-${formState?.currentPage}`,
                ingress,
                !!ccxDetailsInfo
              )}
            >
              <FormattedMessage
                id='cs_perfportal_tx_monitoring_next'
                defaultMessage={getDefaultMessageByID(
                  txMonitoringData,
                  'cs_perfportal_tx_monitoring_next'
                )}
              />
            </Button>
          )}
        </div>
      </div>
    </Box>
  );

  // Form can not be (re)submitted when its state is LOADING or LOADED.
  const isFormLoadingOrLoaded =
    formStatusCodeState === DataState.LOADING ||
    formStatusCodeState === DataState.LOADED;

  const isSaveDraftButtonDisabled =
    isReviewedOrDelivered || isFormLoadingOrLoaded;
  const isDeliverButtonDisabled =
    !canSave || isReviewedOrDelivered || isFormLoadingOrLoaded;
  // defining separate param for email checkbox to decouple it from the logic for the deliver button
  // yet giving the flexibility to differentiate them when needed.
  const isEmailCheckBoxDisabled = isDeliverButtonDisabled;

  return (
    <>
      {txMonitoringData && (
        <Box className={styles.txMonitoringFormContainer}>
          <Row>{dropdowns}</Row>
          {formType && (
            <>
              <Row className={styles.title}>
                <Text type='h400'>
                  {(() => {
                    return formType ===
                      FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW ? (
                      <FormattedMessage
                        id='cs_perfportal_d2as_contact_review_form_title'
                        defaultMessage='D2AS Contact Review Form'
                      />
                    ) : (
                      <FormattedMessage
                        id='cs_perfportal_tx_monitoring_form_title'
                        defaultMessage='Transaction Monitoring Form'
                      />
                    );
                  })()}
                </Text>
              </Row>
              <Row
                className={styles.formStepperContainer}
                alignmentVertical='stretch'
              >
                <Column className={styles.leftCol}>{stepperLeftColumn}</Column>
                <Column className={styles.rightCol}>
                  {stepperRightColumn}
                </Column>
              </Row>
              <div className={styles.buttonsContainer}>
                <Button
                  onClick={handleSaveAsDraft}
                  type='secondary'
                  className={styles.saveButton}
                  disabled={isSaveDraftButtonDisabled}
                  {...csaFields(
                    'button',
                    'save as draft',
                    'cs_perfportal_tx_monitoring_draft',
                    ingress,
                    !!ccxDetailsInfo
                  )}
                >
                  <FormattedMessage
                    id='cs_perfportal_tx_monitoring_draft'
                    defaultMessage={
                      grabExtraFieldByID(
                        txMonitoringData,
                        'cs_perfportal_tx_monitoring_draft'
                      )?.DEFAULT_MESSAGE
                    }
                  />
                </Button>
                <Button
                  data-testid='deliver-button'
                  onClick={openFeedbackModal}
                  className={styles.saveButton}
                  disabled={isDeliverButtonDisabled}
                  {...csaFields(
                    'button',
                    'deliver',
                    'cs_perfportal_tx_monitoring_deliver',
                    ingress,
                    !!ccxDetailsInfo
                  )}
                >
                  <FormattedMessage
                    id='cs_perfportal_tx_monitoring_deliver'
                    defaultMessage={
                      grabExtraFieldByID(
                        txMonitoringData,
                        'cs_perfportal_tx_monitoring_deliver'
                      )?.DEFAULT_MESSAGE
                    }
                  />
                </Button>
                <div className={styles.emailCheckboxContainer}>
                  <Checkbox
                    checked={emailChecked}
                    onChange={setEmailChecked}
                    disabled={isEmailCheckBoxDisabled}
                    data-csa-c-type='element'
                    data-csa-c-slot-id='tx-monitoring-email-notification-slot'
                    data-csa-c-content-id='tx-monitoring-email-notification-checkbox'
                    data-csa-c-checked={`${emailChecked}`}
                  >
                    <FormattedMessage
                      id='cs_perfportal_tx-monitoring_email_notification'
                      defaultMessage='E-mail summary to associate'
                    />
                  </Checkbox>
                </div>
              </div>
            </>
          )}
        </Box>
      )}
      {!txMonitoringData && <Loader data-testid='loader' />}
      {contactId && (
        <ContactReviewFeedbackModal
          open={showContactReviewFeedbackModal?.open}
          parentValues={
            contactList?.filter(c => c.contactID === contactId)?.[0] ?? {}
          }
          parentUserName={profile.user?.userName}
          parentUserID={userId ?? ''}
          onFeedbackSubmitted={handleFeedbackSubmitted}
          contactTab={ContactViewTabObject.RECOMMENDED}
          contactCategory='Corrective'
          ingress={ingress}
        />
      )}
      <TimeoutToast
        show={isAdmin && showErrorToast}
        message={exceptionMessage}
        duration={3000}
        type='error'
        onClose={(): void => {
          setShowErrorToast(false);
        }}
      />
      <DraftSavedToast
        show={showDraftSavedToast}
        onClose={() => setShowDraftSavedToast(false)}
      />
    </>
  );
};

export default TransactionMonitoringForm;
